/**
 * The owner's bulk sends in this workspace, newest first — each a link to its
 * overview. The way back to a send that needs none of its copies to exist:
 * starting a send retires its template, and a send paused before its first
 * copy (or one where everyone failed while copying) has no copy in the
 * envelope list to lead back to it.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { DocumentSignBulkBatchSummary } from '@/lib/api';
import { useBulkBatches } from '../hooks/useBulkSend';

/** Sends shown before "Show all". */
const SHOWN = 3;

function progress(b: DocumentSignBulkBatchSummary): string {
  const failed = b.failed > 0 ? ` · ${b.failed} failed` : '';
  return b.status === 'processing'
    ? `Sending — ${b.sent} of ${b.total} sent${failed}`
    : `Done — ${b.sent} of ${b.total} sent${failed}`;
}

export function BulkSendsSection({ holderId }: { holderId: string }) {
  const navigate = useNavigate();
  const { bulkSends, hasOlder, loadingOlder, loadOlder } = useBulkBatches(holderId);
  const [showAll, setShowAll] = useState(false);
  if (bulkSends.length === 0) return null;
  const shown = showAll ? bulkSends : bulkSends.slice(0, SHOWN);

  return (
    <section aria-labelledby="bulk-sends-heading" className="border rounded-md p-3 space-y-2">
      <h2 id="bulk-sends-heading" className="text-sm font-semibold flex items-center gap-2">
        <Layers className="h-4 w-4" aria-hidden />
        Your bulk sends
      </h2>
      <ul className="space-y-1">
        {shown.map((b) => (
          <li key={b.batchId}>
            <button
              type="button"
              className="w-full text-left rounded px-2 py-1.5 hover:bg-accent/40 flex flex-wrap items-baseline justify-between gap-x-3"
              onClick={() => navigate(`/a/arche_document_sign/bulk/${b.batchId}`)}
              aria-label={`Open bulk send ${b.title || 'Untitled envelope'}`}
            >
              <span className="font-medium truncate">{b.title || 'Untitled envelope'}</span>
              <span className="text-xs text-muted-foreground">
                {progress(b)} · {new Date(b.createdAt).toLocaleDateString()}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        {(bulkSends.length > SHOWN || hasOlder) && (
          <Button variant="ghost" size="sm" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Show fewer' : hasOlder ? 'Show all' : `Show all ${bulkSends.length}`}
          </Button>
        )}
        {/* Every send stays reachable, however many there are. */}
        {showAll && hasOlder && (
          <Button variant="ghost" size="sm" onClick={loadOlder} disabled={loadingOlder}>
            {loadingOlder ? 'Loading…' : 'Older sends'}
          </Button>
        )}
      </div>
    </section>
  );
}
